/**
 * Interceptor per gestire automaticamente gli errori di autenticazione
 * 
 * Intercetta gli errori AUTH_TOKEN_MISSING e AUTH_TOKEN_INVALID e:
 * - Elimina il token dall'storage
 * - Reindirizza automaticamente alla pagina di login
 * - Mostra un messaggio informativo all'utente
 */

import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { TokenStorageService } from "./token-storage.service";
import { LoggerService } from "src/ancestors/logger.service";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { ToastService } from "src/app/shared/services/toast.service";
import { TranslateService } from "src/app/shared/util/translate.service";

export interface AuthErrorInterceptorConfig {
  /** Lista degli errori che causano logout automatico */
  logoutErrors: string[];
  /** Messaggio da mostrare all'utente quando viene fatto logout */
  logoutMessage?: string;
  /** URL di reindirizzamento dopo il logout */
  loginUrl?: string;
}

@Injectable({
  providedIn: "root"
})
export class AuthErrorInterceptorService {
  private router = inject(Router);
  private tokenStorage = inject(TokenStorageService);
  private logger = inject(LoggerService);
  private envService = inject(EnvironmentLoaderService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  private readonly config: AuthErrorInterceptorConfig = {
    logoutErrors: ["AUTH_TOKEN_MISSING", "AUTH_TOKEN_INVALID", "AUTH_TOKEN_EXPIRED"],
    logoutMessage: "La tua sessione è scaduta. Verrai reindirizzato alla pagina di login.",
    loginUrl: "/login"
  };

  // Flag per evitare chiamate API a cascata quando il token è scaduto
  private isLogoutInProgress = false;
  private logoutPromise: Promise<void> | null = null;

  /**
   * Verifica se il logout è in corso e blocca chiamate API a cascata
   * @returns true se il logout è già in corso
   */
  public isLogoutInProgressState(): boolean {
    return this.isLogoutInProgress;
  }

  /**
   * Resetta lo stato di logout (chiamato dopo login completato)
   */
  public resetLogoutState(): void {
    this.isLogoutInProgress = false;
    this.logoutPromise = null;
    }

  /**
   * Controlla se l'errore richiede logout automatico
   * @param error - Errore da verificare
   * @returns true se l'errore richiede logout
   */
  public isAuthError(error: unknown): boolean {
    if (!error || typeof error !== "object") {
      return false;
    }

    // Controlla se è un errore HTTP con codice di stato non autorizzato
    if ("status" in error && (error as Record<string, unknown>)["status"] === 401) {
      return true;
    }

    // Controlla se è un errore applicativo con codici specifici
    if ("applicationError" in error) {
      const applicationError = (error as Record<string, unknown>)["applicationError"];
      return this.config.logoutErrors.includes(String(applicationError));
    }

    // Controlla se è un errore con messaggio specifico
    if ("error" in error) {
      const errorString = String((error as Record<string, unknown>)["error"]);
      return this.config.logoutErrors.some(authError => 
        errorString.includes(authError)
      );
    }

    // Controlla se è un errore con errorKey
    if ("errorKey" in error) {
      const errorKey = (error as Record<string, unknown>)["errorKey"];
      return this.config.logoutErrors.includes(String(errorKey));
    }

    return false;
  }

  /**
   * Esegue il logout automatico dell'utente (con circuit breaker per evitare chiamate multiple)
   * @param error - Errore che ha causato il logout
   * @param context - Contesto dell'errore (servizio, metodo)
   */
  public async handleAuthError(error: unknown, context?: { serviceName?: string; method?: string }): Promise<void> {
    // Se il logout è già in esecuzione, aspetta che finisca invece di avviarne uno nuovo
    if (this.logoutPromise) {
      await this.logoutPromise;
      return;
    }

    // Crea e memorizza la promise del logout per altre chiamate concorrenti  
    // (Il flag isLogoutInProgress è già stato impostato in interceptError)
    this.logoutPromise = this.performLogout(error, context);

    try {
      await this.logoutPromise;
    } catch (logoutError) {
      this.logger.error("[AuthErrorInterceptor] Errore durante logout:", String(logoutError));
      // Il flag rimane true per bloccare altre chiamate fino al prossimo login
    }
  }

  /**
   * Esegue effettivamente il logout una sola volta
   * @param error - Errore originale
   * @param context - Contesto dell'errore
   */
  private async performLogout(error: unknown, context?: { serviceName?: string; method?: string }): Promise<void> {
    try {
      this.logger.warn(
        `[AuthErrorInterceptor] Esecuzione logout automatico per errore AUTH. Context: ${JSON.stringify(context) ?? "none"}`
      );

      // Ottieni la configurazione dell'ambiente
      const env = this.envService.getEnvConfig();
      const tokenKey = env.loginType === "local" ? env.localTokenKey : env.ssoTokenKey;

      // Verifica se l'utente è attualmente autenticato
      const currentToken = this.tokenStorage.retriveToken(tokenKey);
      if (currentToken) {
        // Elimina il token dall'storage solo se presente
        this.tokenStorage.deleteToken(tokenKey);
      }

      // Salva l'URL corrente per il reindirizzamento post-login
      const currentUrl = this.router.url;
      if (currentUrl && currentUrl !== this.config.loginUrl) {
        sessionStorage.setItem("originalRequestedUrl", currentUrl);
      }

      // Reindirizza alla pagina di login
      const loginUrl = this.config.loginUrl ?? env.loginUrl ?? "/login";
      await this.router.navigate([loginUrl]);

      this.logger.info("[AuthErrorInterceptor] Logout automatico completato, utente reindirizzato al login");

    } catch (redirectError) {
      this.logger.error("[AuthErrorInterceptor] Errore durante il logout automatico:", String(redirectError));

      // Fallback: ricarica la pagina per forzare il login
      window.location.href = this.config.loginUrl ?? "/login";
    }
  }

  /**
   * Intercetta e gestisce gli errori delle chiamate HTTP
   * @param error - Errore HTTP ricevuto
   * @param serviceName - Nome del servizio che ha generato l'errore
   * @param method - Metodo che ha generato l'errore
   * @returns Promise che si risolve se l'errore è gestito
   */
  public async interceptError(
    error: unknown, 
    serviceName?: string, 
    method?: string
  ): Promise<void> {
    if (this.isAuthError(error)) {
      // ⚡ CRITICO: Imposta il flag IMMEDIATAMENTE per bloccare chiamate successive
      // Questo previene il race condition tra chiamate parallele
      if (!this.isLogoutInProgress) {
        this.isLogoutInProgress = true;
        this.logger.warn(
          `[AuthErrorInterceptor] Primo errore AUTH rilevato, attivazione circuit breaker. Context: ${JSON.stringify({ serviceName, method }) ?? "none"}`
        );
      }
      
      await this.handleAuthError(error, { serviceName, method });
    }
  }

  /**
   * Configura l'interceptor con parametri personalizzati
   * @param config - Configurazione personalizzata
   */
  public configure(config: Partial<AuthErrorInterceptorConfig>): void {
    Object.assign(this.config, config);
  }

  /**
   * Ottiene la configurazione corrente dell'interceptor
   */
  public getConfig(): Readonly<AuthErrorInterceptorConfig> {
    return { ...this.config };
  }
}
